import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Search,
  Wallet,
  RefreshCw,
  Download,
  ChevronLeft,
  ChevronRight,
  Eye,
  Filter,
  X,
  CheckCircle,
  XCircle,
  Clock,
  ArrowUpRight,
  Building2,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatAmount = (amount, currency = 'KZT') => {
  if (!amount) return '0';
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: currency === 'KZT' ? 'KZT' : currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleString('ru-RU');
};

const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { label: 'Ожидает', className: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Clock },
    approved: { label: 'Одобрено', className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
    rejected: { label: 'Отклонено', className: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
    processing: { label: 'В обработке', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: RefreshCw },
    completed: { label: 'Выполнено', className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  };
  const config = statusConfig[status] || { label: status || 'N/A', className: 'bg-slate-500/20 text-slate-400', icon: Clock };
  const Icon = config.icon;
  return (
    <Badge variant="outline" className={`${config.className} flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

const TypeBadge = ({ type }) => {
  const typeConfig = {
    withdrawal: { label: 'Вывод', className: 'bg-purple-500/20 text-purple-400' },
    transfer: { label: 'Перевод', className: 'bg-blue-500/20 text-blue-400' },
    settlement: { label: 'Расчёт', className: 'bg-cyan-500/20 text-cyan-400' },
  };
  const config = typeConfig[type] || { label: type || 'N/A', className: 'bg-slate-500/20 text-slate-400' };
  return <Badge className={config.className}>{config.label}</Badge>;
};

const PayoutsPage = () => {
  const { token, hasPermission } = useAuth();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [stats, setStats] = useState({ pending: 0, approved: 0, processing: 0, completed: 0, rejected: 0 });
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const canApprove = hasPermission('payouts', 'approve');
  const canReject = hasPermission('payouts', 'reject');

  const [filterForm, setFilterForm] = useState({
    status: 'all',
    payout_type: 'all',
    merchant_id: '',
  });

  const fetchPayouts = async (page = 1, currentFilters = filters) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pagination.pageSize.toString(),
      });

      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await fetch(`${API_URL}/api/payouts/search?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to fetch payouts');

      const data = await response.json();
      setPayouts(data.payouts || []);
      setPagination({
        page: data.page,
        pageSize: data.page_size,
        total: data.total,
        totalPages: data.total_pages,
      });
    } catch (err) {
      setError(err.message);
      setPayouts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/payouts/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchPayoutDetails = async (payoutId) => {
    try {
      const response = await fetch(`${API_URL}/api/payouts/${payoutId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedPayout(data);
      }
    } catch (err) {
      console.error('Error fetching payout details:', err);
    }
  };

  useEffect(() => {
    fetchPayouts();
    fetchStats();
  }, []);

  const handleSearch = () => {
    const newFilters = { ...filterForm };
    setFilters(newFilters);
    fetchPayouts(1, newFilters);
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    setFilterForm({
      status: 'all',
      payout_type: 'all',
      merchant_id: '',
    });
    setFilters({});
    fetchPayouts(1, {});
  };

  const handlePageChange = (newPage) => {
    fetchPayouts(newPage);
  };

  const handleApprove = async (payoutId) => {
    if (!window.confirm('Вы уверены, что хотите одобрить эту выплату?')) return;

    try {
      setActionLoading(true);
      const response = await fetch(`${API_URL}/api/payouts/${payoutId}/approve`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Выплата одобрена');
        setSelectedPayout(null);
        fetchPayouts(pagination.page);
        fetchStats();
      } else {
        toast.error(data.message || 'Ошибка');
      }
    } catch (err) {
      toast.error('Ошибка при одобрении');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (payoutId) => {
    if (!window.confirm('Вы уверены, что хотите отклонить эту выплату?')) return;

    try {
      setActionLoading(true);
      const response = await fetch(`${API_URL}/api/payouts/${payoutId}/reject`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason: 'Отклонено администратором' }),
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Выплата отклонена');
        setSelectedPayout(null);
        fetchPayouts(pagination.page);
        fetchStats();
      } else {
        toast.error(data.message || 'Ошибка');
      }
    } catch (err) {
      toast.error('Ошибка при отклонении');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="payouts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Выплаты</h1>
          <p className="text-slate-400 mt-1">
            Всего: {pagination.total.toLocaleString('ru-RU')} выплат
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4 mr-2" />
            Фильтры
          </Button>
          <Button
            onClick={async () => {
              try {
                const response = await api.get("/export/payouts", { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `payouts_${new Date().toISOString().slice(0, 10)}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                toast.success("Экспорт выплат завершён");
              } catch (err) {
                toast.error("Ошибка экспорта");
              }
            }}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
            data-testid="export-payouts-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            Экспорт
          </Button>
          <Button
            onClick={() => { fetchPayouts(pagination.page); fetchStats(); }}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-amber-500/10 border-amber-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" />
              <div>
                <p className="text-xs text-amber-400">Ожидают</p>
                <p className="text-xl font-bold text-amber-300">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-500/10 border-emerald-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              <div>
                <p className="text-xs text-emerald-400">Одобрено</p>
                <p className="text-xl font-bold text-emerald-300">{stats.approved}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-500/10 border-blue-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-blue-400" />
              <div>
                <p className="text-xs text-blue-400">В обработке</p>
                <p className="text-xl font-bold text-blue-300">{stats.processing}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-500/10 border-emerald-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <ArrowUpRight className="w-5 h-5 text-emerald-400" />
              <div>
                <p className="text-xs text-emerald-400">Выполнено</p>
                <p className="text-xl font-bold text-emerald-300">{stats.completed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-500/10 border-red-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-400" />
              <div>
                <p className="text-xs text-red-400">Отклонено</p>
                <p className="text-xl font-bold text-red-300">{stats.rejected}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {error && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-400 text-sm">
          {error}
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-white">Фильтры</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowFilters(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Статус</Label>
                <Select
                  value={filterForm.status}
                  onValueChange={(value) => setFilterForm({ ...filterForm, status: value })}
                >
                  <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                    <SelectValue placeholder="Все статусы" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все</SelectItem>
                    <SelectItem value="0">Ожидает</SelectItem>
                    <SelectItem value="1">Одобрено</SelectItem>
                    <SelectItem value="2">Отклонено</SelectItem>
                    <SelectItem value="3">В обработке</SelectItem>
                    <SelectItem value="4">Выполнено</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Тип</Label>
                <Select
                  value={filterForm.payout_type}
                  onValueChange={(value) => setFilterForm({ ...filterForm, payout_type: value })}
                >
                  <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                    <SelectValue placeholder="Все типы" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все</SelectItem>
                    <SelectItem value="0">Вывод</SelectItem>
                    <SelectItem value="1">Перевод</SelectItem>
                    <SelectItem value="2">Расчёт</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">ID мерчанта</Label>
                <Input
                  type="number"
                  placeholder="Merchant ID"
                  value={filterForm.merchant_id}
                  onChange={(e) => setFilterForm({ ...filterForm, merchant_id: e.target.value })}
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-700">
              <Button onClick={handleSearch} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                <Search className="w-4 h-4 mr-2" />
                Применить
              </Button>
              <Button variant="outline" onClick={handleClearFilters} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                Сбросить
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payouts Table */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
          ) : payouts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <Wallet className="w-12 h-12 mb-4 opacity-50" />
              <p>Выплаты не найдены</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left p-4 text-sm font-medium text-slate-400">ID</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Тип</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Статус</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Мерчант</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Баланс</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Дата</th>
                    <th className="text-right p-4 text-sm font-medium text-slate-400">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((payout) => (
                    <tr
                      key={payout.id}
                      className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors"
                    >
                      <td className="p-4">
                        <span className="text-white font-medium">#{payout.id}</span>
                      </td>
                      <td className="p-4">
                        <TypeBadge type={payout.type} />
                      </td>
                      <td className="p-4">
                        <StatusBadge status={payout.status} />
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-500" />
                          <span className="text-slate-300 text-sm">{payout.merchant_name || `ID: ${payout.merchant_id}`}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-white font-medium">
                          {formatAmount(payout.source_balance, payout.source_currency)}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-slate-400 text-sm">{formatDate(payout.created_at)}</span>
                      </td>
                      <td className="p-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => fetchPayoutDetails(payout.id)}
                          className="text-slate-400 hover:text-white"
                          data-testid={`view-payout-${payout.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-700">
              <p className="text-sm text-slate-400">
                Страница {pagination.page} из {pagination.totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payout Detail Dialog */}
      <Dialog open={!!selectedPayout} onOpenChange={() => setSelectedPayout(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-emerald-500" />
              Выплата #{selectedPayout?.id}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Детальная информация о выплате
            </DialogDescription>
          </DialogHeader>

          {selectedPayout && (
            <div className="space-y-4 mt-4">
              {/* Status & Type */}
              <div className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <TypeBadge type={selectedPayout.type} />
                  <StatusBadge status={selectedPayout.status} />
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-400">Баланс кошелька</p>
                  <p className="text-xl font-bold text-emerald-400">
                    {formatAmount(selectedPayout.source_balance, selectedPayout.source_currency)}
                  </p>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">Мерчант</p>
                  <p className="text-sm text-white">{selectedPayout.merchant_name || `ID: ${selectedPayout.merchant_id}`}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">Валюта</p>
                  <p className="text-sm text-white">{selectedPayout.source_currency || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">ID кошелька</p>
                  <p className="text-sm text-white font-mono">{selectedPayout.source_wallet_id || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">Токен</p>
                  <p className="text-sm text-white font-mono truncate">{selectedPayout.token || 'N/A'}</p>
                </div>
              </div>

              {/* Dates */}
              <div className="border-t border-slate-700 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Создано</p>
                    <p className="text-sm text-white">{formatDate(selectedPayout.created_at)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Обновлено</p>
                    <p className="text-sm text-white">{formatDate(selectedPayout.updated_at)}</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              {selectedPayout.status === 'pending' && (canApprove || canReject) && (
                <div className="flex gap-2 pt-4 border-t border-slate-700">
                  {canApprove && (
                    <Button
                      onClick={() => handleApprove(selectedPayout.id)}
                      className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                      disabled={actionLoading}
                    >
                      {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      Одобрить
                    </Button>
                  )}
                  {canReject && (
                    <Button
                      onClick={() => handleReject(selectedPayout.id)}
                      variant="destructive"
                      className="flex-1"
                      disabled={actionLoading}
                    >
                      {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                      Отклонить
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayoutsPage;
